
import React, { useContext, useEffect, useState } from "react";
import { Scrollbars } from "react-custom-scrollbars-2";
import { Card, CardBody } from "@windmill/react-ui";
import { FiX } from "react-icons/fi";
import { useForm } from "react-hook-form";

import { SidebarContext } from "context/SidebarContext";
import useToggleDrawer from "hooks/useToggleDrawer";
import TrustedCompaniesServices from "services/TrustedCompaniesServices";

import Title from "components/form/Title";
import LabelArea from "components/form/LabelArea";
import InputArea from "components/form/InputArea";
import Error from "components/form/Error";
import DrawerButton from "components/form/DrawerButton";
import UploaderAds from "components/image-uploader/UploaderAds";

import { notifyError, notifySuccess } from "utils/toast";

const TrustedCompanyDrawer = ({ id, isViewMode = false }) => {
    const { toggleDrawer, setIsUpdate } = useContext(SidebarContext);
    const { setServiceId } = useToggleDrawer();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [companyLogo, setCompanyLogo] = useState("");

    const {
        register,
        handleSubmit,
        setValue,
        reset,
        trigger,
        formState: { errors },
    } = useForm({
        defaultValues: {
            company_name: "",
            company_logo: "",
        },
    });


    useEffect(() => {
        let isMounted = true;

        const fetchCompany = async () => {
            if (!id) {
                reset({
                    company_name: "",
                    company_logo: "",
                });

                setCompanyLogo("");
                setLoading(false);

                return;
            }

            try {
                setLoading(true);

                const response =
                    await TrustedCompaniesServices.getTrustedCompanyById(id);

                const company = response?.data?.data ?? response?.data;

                if (!isMounted || !company) {
                    return;
                }

                reset({
                    company_name: company.company_name || "",
                    company_logo: company.company_logo || "",
                });

                setCompanyLogo(company.company_logo || "");
            } catch (error) {
                if (isMounted) {
                    notifyError(
                        error?.response?.data?.message ||
                        error?.message ||
                        "Unable to load company details."
                    );
                }
            } finally {
                if (isMounted) {
                    setLoading(false);
                }
            }
        };

        fetchCompany();

        return () => {
            isMounted = false;
        };
    }, [id, reset]);


    const handleLogoChange = (imagePath) => {
        const logoPath = Array.isArray(imagePath)
            ? imagePath[0] || ""
            : imagePath || "";

        setCompanyLogo(logoPath);

        setValue("company_logo", logoPath, {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const handleCancelImage = () => {
        setCompanyLogo("");

        setValue("company_logo", "", {
            shouldDirty: true,
            shouldValidate: true,
        });
    };

    const handleCancel = () => {
        setServiceId("");
        toggleDrawer();
    };

    const onSubmit = async (formData) => {
        if (isViewMode) {
            return;
        }

        const isNameValid = await trigger("company_name");

        if (!isNameValid) {
            return;
        }

        const companyName = formData.company_name?.trim();
        const logo = formData.company_logo || companyLogo;

        if (!companyName) {
            notifyError("Company name is required.");
            return;
        }

        if (!logo) {
            notifyError("Please upload a company logo.");
            return;
        }

        const payload = {
            company_name: companyName,
            company_logo: logo,
        };

        try {
            setSaving(true);

            const response = id
                ? await TrustedCompaniesServices.editTrustedCompany(id, payload)
                : await TrustedCompaniesServices.createTrustedCompany(payload);

            notifySuccess(
                response?.data?.message ||
                response?.message ||
                (id
                    ? "Trusted company updated successfully."
                    : "Trusted company created successfully.")
            );

            setIsUpdate(true);
            setServiceId("");
            toggleDrawer();
        } catch (error) {
            notifyError(
                error?.response?.data?.message ||
                error?.message ||
                "Unable to save trusted company."
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="w-full relative p-6 border-b border-gray-100 bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <Title
                    register={register}
                    title={
                        isViewMode
                            ? "View Trusted Company"
                            : id
                                ? "Edit Trusted Company"
                                : "Add Trusted Company"
                    }
                />
            </div>

            <Scrollbars className="w-full md:w-7/12 lg:w-8/12 xl:w-8/12 relative dark:bg-gray-700 dark:text-gray-200">
                <Card className="flex-grow scrollbar-hide w-full max-h-full">
                    <CardBody>
                        <form onSubmit={handleSubmit(onSubmit)}>
                            <div className="pt-5 flex-grow scrollbar-hide w-full max-h-full pb-20">
                                {loading ? (
                                    <div className="flex justify-center items-center py-10">
                                        <span className="text-sm text-gray-500">
                                            Loading company details...
                                        </span>
                                    </div>
                                ) : (
                                    <div className="grid grid-cols-12 gap-5 mb-6 items-start">
                                        <div className="col-span-12">
                                            <LabelArea label="Company Name" required />

                                            <InputArea
                                                trigger={trigger}
                                                register={register}
                                                label="Company Name"
                                                name="company_name"
                                                type="text"
                                                placeholder="Enter company name"
                                                required={!isViewMode}
                                                disabled={isViewMode || saving}
                                            />

                                            <Error errorName={errors.company_name} />
                                        </div>

                                        <div className="col-span-12">
                                            <LabelArea label="Company Logo" required={!isViewMode} />

                                            {companyLogo ? (
                                                <div className="flex items-center gap-3">
                                                    <img
                                                        src={
                                                            companyLogo.startsWith("http")
                                                                ? companyLogo
                                                                : `${process.env.REACT_APP_API_IMAGE_URL}${companyLogo}`
                                                        }
                                                        alt="Trusted company logo"
                                                        style={{
                                                            objectFit: "contain",
                                                            width: "350px",
                                                            height: "150px",
                                                            borderRadius: "5px",
                                                        }}
                                                    />

                                                    {!isViewMode && (
                                                        <FiX
                                                            size={20}
                                                            onClick={handleCancelImage}
                                                            style={{
                                                                cursor: saving ? "not-allowed" : "pointer",
                                                                opacity: saving ? 0.5 : 1,
                                                            }}
                                                        />
                                                    )}
                                                </div>
                                            ) : isViewMode ? (
                                                <p className="text-sm text-gray-500">
                                                    No company logo available.
                                                </p>
                                            ) : (
                                                <UploaderAds
                                                    is_multiple={false}
                                                    folder="trusted-companies"
                                                    name_merge="company_logo"
                                                    imageUrl={companyLogo}
                                                    setImageUrl={handleLogoChange}
                                                    onImageUpload={handleLogoChange}
                                                    no_of_image={1}
                                                    required={true}
                                                />
                                            )}

                                            {/* {!companyLogo && !isViewMode && (
                        <p className="mt-1 text-xs text-red-500">
                          Company logo is required.
                        </p>
                      )} */}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {!isViewMode && (
                                <DrawerButton
                                    isSubmitting={loading || saving}
                                    id={id}
                                    title={id ? "Update" : "Submit"}
                                />
                            )}

                            {isViewMode && (
                                <div className="flex justify-end pt-4">
                                    <button
                                        type="button"
                                        onClick={handleCancel}
                                        className="px-5 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200"
                                    >
                                        Close
                                    </button>
                                </div>
                            )}
                        </form>
                    </CardBody>
                </Card>
            </Scrollbars>
        </>
    );
};

export default TrustedCompanyDrawer;
